import { DotsLoader } from '@eppendorf/vnls-react-components';
import { useTranslation } from 'react-i18next';

import { LoadingErrorHint } from '$components/loading-error-hint/loading-error-hint';
import { useGetRunRecordTelemetry } from '$features/report-hub/run-record/run-record-detail.api';
import { RunRecordRuntimeChart } from '$features/report-hub/run-record/run-record-runtime-chart';

const SERIES_COLORS: Record<string, string> = {
  Temperature: '#3b82f6',
  Speed: '#a855f7',
};

const DEFAULT_SERIES_COLOR = '#6b7280';

interface RunRecordHistoryChartsProps {
  reportId: string;
}

/**
 * Renders the temperature and speed charts for a run record.
 *
 * the x-axis is runtime starting at 0:00 — that's handled by
 * `RunRecordRuntimeChart`. There is no setpoint toggle (the design intentionally
 * omits it for run records).
 */
export function RunRecordHistoryCharts({ reportId }: RunRecordHistoryChartsProps) {
  const { t } = useTranslation();
  const { data, isLoading, isError, error } = useGetRunRecordTelemetry(reportId);
  if (isLoading) {
    return <DotsLoader className="m-top-xl m-bottom-xl" />;
  }

  // Charts are captured at run-record ingestion time; a 404 means the upload
  // pipeline hasn't written history.json yet. Same UX as events.
  const isNotReadyYet = isError && error?.response?.status === 404;

  if (isNotReadyYet) {
    return (
      <div className="flex flex__ai--center flex__jc--center color-gray-700 p-xxl">
        {t('runRecordDetail.charts.notReadyYet')}
      </div>
    );
  }

  if (isError || !data) {
    return <LoadingErrorHint className="m-top-m" />;
  }

  if (!data.series.length) {
    return (
      <span className="color-gray-500">
        {t('runRecordDetail.charts.noChartsAvailable')}
      </span>
    );
  }
  // Runtime chart is always the same. There's no need to show it.
  const filteredData = data.series.filter((series) => series.telemetryId !== 'Runtime');

  return (
    <div>
      {filteredData.map((series) => (
        <RunRecordRuntimeChart
          key={series.telemetryId}
          series={series}
          runtimeMs={data.runtimeMs}
          color={SERIES_COLORS[series.subject] ?? DEFAULT_SERIES_COLOR}
        />
      ))}
    </div>
  );
}
